import { Component, DOCUMENT, Inject, inject, PLATFORM_ID, Renderer2, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Api } from '../../services/api';
import { Meta, Title } from '@angular/platform-browser';
import { ArticlesItems, GetArticleParam } from '../../interface/articles';
import { catchError, map, of, tap, timeout } from 'rxjs';
import { Navbar } from '../navbar/navbar';
import { Footer } from '../footer/footer';
import { DatePipe, isPlatformBrowser } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { WidgetRelated } from '../widget-related/widget-related';
import { Widgets } from '../widgets/widgets';
import { Canonical } from '../../services/canonical';
import { PageNotFound } from '../page-not-found/page-not-found';
import { TruncatePipe } from '../../pipes/truncate-pipe';

@Component({
  selector: 'app-article',
  imports: [
    Navbar,
    Footer,
    Widgets,
    WidgetRelated,
    DatePipe,
    PageNotFound,
    RouterLink,
    TruncatePipe,
    NgxSkeletonLoaderModule
  ],
  templateUrl: './article.html',
  styleUrl: './article.css',
  host: { ngSkipHydration: 'true' }
})
export class Article {
  title = inject(Title);
  meta = inject(Meta);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  isLoading = signal(false);
  isUndefined = signal(false);
  article = signal<ArticlesItems | undefined>(undefined);
  private canonical = inject(Canonical);
  private api = inject(Api);
  private platformId = inject(PLATFORM_ID);
  
  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  summarize(obj: string) : void {
    const prompt = encodeURIComponent(this.article()?.prompt!);
    const bot = obj;

    if(bot === 'ChatGPT') {
      window.open(`https://chatgpt.com/?q=${prompt}`, '_blank');
    }

    if(bot === 'Google Gemini') {
      window.open(`https://www.google.com/search?udm=50&aep=11&q=${prompt}`, '_blank');
    }

    if(bot === 'Claude') {
      window.open(`https://claude.ai/new?q=${prompt}`, '_blank');
    }

    if(bot === 'Perplexity') {
      window.open(`https://www.perplexity.ai/search/new?q=${prompt}`, '_blank');
    }

    if(bot === 'Grok') {
      window.open(`https://x.com/i/grok?text=${prompt}`, '_blank');
    }
  }

  private renderFacebookShareButton(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const parse = () => {
      const fb = (window as any).FB;
      if (typeof fb !== 'undefined' && fb.XFBML) {
        fb.XFBML.parse();
        return true;
      }
      return false;
    };

    setTimeout(() => {
      if (parse()) return;

      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (parse() || attempts >= 20) {
          clearInterval(interval);
        }
      }, 200);
    }, 100);
  }

  private renderTwitterShareButton(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const parse = () => {
      const twttr = (window as any).twttr;
      if (typeof twttr !== 'undefined' && twttr.widgets) {
        twttr.widgets.load();
        return true;
      }
      return false;
    };

    setTimeout(() => {
      if (parse()) return;

      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (parse() || attempts >= 20) {
          clearInterval(interval);
        }
      }, 200);
    }, 100);
  }

  private setMetaTags(obj?: ArticlesItems) : void {
    this.title.setTitle(obj?.setTitle!);
    this.canonical.setCanonicalUrl(obj?.canonical!);
    this.meta.addTags(JSON.parse(obj?.meta!));
  }

  private setSchemaStructuredData(obj?: ArticlesItems) : void {
    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = obj?.schema!;
    this.renderer.appendChild(this.document.head, script);
  }

  private initialize(obj: GetArticleParam) : void {
    this.isLoading.set(true);
    this.isUndefined.set(false);

    this.api.getArticle(obj)
    .pipe(
      timeout(10000),
      map((obj) => obj),
      catchError(() => of(undefined)),
      tap(() => this.isLoading.set(false))
    ).subscribe(article => {
      if(!article){
        this.isUndefined.set(true);
      }else{
        this.setMetaTags(article);
        this.setSchemaStructuredData(article);
        this.article.set(article);
        this.renderFacebookShareButton();
        this.renderTwitterShareButton();
      }
    });
  }

  ngOnInit() : void {
    this.activatedRoute.paramMap.subscribe(param => {
      this.initialize({
        category: param.get('category'),
        slug: param.get('slug')
      });
    });
  }
}
